import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { StatusIcon } from "@/components/StatusIcon";
import NZTerritoryMap from "@/components/NZTerritoryMap";
import {
  LayoutDashboard, Users, Flower2, Boxes, BarChart3, Calendar,
  BookOpen, MessageSquare, Sprout, TrendingUp, Truck, Wallet,
  Network, Settings, LogOut, ChevronRight, Clock,
} from "lucide-react";

const SECTIONS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, iconKey: "grower.dashboard", live: true },
  { id: "subscribers", label: "My Subscribers", icon: Users, iconKey: "grower.subscribers" },
  { id: "stem_forecast", label: "Stem Forecast", icon: Flower2, iconKey: "grower.forecast" },
  { id: "allocation", label: "Allocation", icon: Boxes, iconKey: "grower.allocation" },
  { id: "harvest_reports", label: "Harvest Reports", icon: BarChart3, iconKey: "grower.harvest" },
  { id: "availability", label: "Availability", icon: Calendar, iconKey: "grower.availability" },
  { id: "guide", label: "Growers Guide", icon: BookOpen, iconKey: "grower.guide" },
  { id: "messages", label: "Messages", icon: MessageSquare, iconKey: "grower.messages" },
  { id: "profile", label: "Farm Profile", icon: Sprout, iconKey: "grower.profile", live: true },
  { id: "growth", label: "Growth Tracker", icon: TrendingUp, iconKey: "grower.growth" },
  { id: "delivery", label: "Delivery Schedule", icon: Truck, iconKey: "grower.delivery" },
  { id: "earnings", label: "Earnings & Payouts", icon: Wallet, iconKey: "grower.earnings" },
  { id: "network", label: "Grower Network", icon: Network, iconKey: "grower.network" },
  { id: "settings", label: "Settings", icon: Settings, iconKey: "grower.settings", live: true },
];

export default function GrowerPortalPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/grower/dashboard");
        setDashboard(data);
      } catch (e) {
        const status = e.response?.status;
        const detail = e.response?.data?.detail || "Could not load dashboard";
        if (status === 404) setError("__no_application__");
        else if (status === 403) setError("__not_a_grower__");
        else setError(detail);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="club-theme min-h-screen">
        <PublicHeader />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center font-serif italic text-[var(--bloom-ink-soft)]">
          Loading your grower portal...
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (error || !dashboard) {
    const isNoApp = error === "__no_application__";
    const isNotGrower = error === "__not_a_grower__";
    return (
      <div className="club-theme min-h-screen">
        <PublicHeader />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="grower-portal-error">
          {isNoApp && (
            <>
              <h1 className="font-serif text-4xl mb-3">Complete your application first</h1>
              <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-6">
                The Grower Portal opens once your Stage 3 application is submitted and approved.
              </p>
              <button onClick={() => navigate("/application")} className="bloom-btn bloom-btn-primary" data-testid="goto-application">
                Continue my application
              </button>
            </>
          )}
          {isNotGrower && (
            <>
              <h1 className="font-serif text-4xl mb-3">Growers only</h1>
              <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-6">
                This area is for Founding Growers. Subscribers can find their portal at /subscriber.
              </p>
              <button onClick={() => navigate("/subscriber")} className="bloom-btn bloom-btn-primary">
                Go to subscriber portal
              </button>
            </>
          )}
          {!isNoApp && !isNotGrower && (
            <>
              <h1 className="font-serif text-4xl mb-3">Hmm.</h1>
              <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-6">{error}</p>
              <button onClick={() => navigate("/portal")} className="bloom-btn bloom-btn-primary">Back to applicant portal</button>
            </>
          )}
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="club-theme min-h-screen flex flex-col" data-testid="grower-portal">
      <PublicHeader />
      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className="w-64 flex-shrink-0 border-r border-[var(--bloom-border)] bg-[var(--bloom-bg)] hidden md:flex flex-col sticky top-0 self-start h-[calc(100vh)]">
          <div className="p-6 border-b border-[var(--bloom-border)]">
            <div className="font-serif text-base leading-tight uppercase tracking-[0.04em]">Grower Portal</div>
            <div className="font-serif italic text-sm leading-tight text-[var(--bloom-ink-soft)] mt-0.5">{dashboard.application?.farm_name || user?.name}</div>
          </div>
          <nav className="flex-1 p-3 overflow-y-auto" data-testid="grower-nav">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const isActive = s.id === active;
              return (
                <button
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 mb-1 transition ${
                    isActive
                      ? "bg-[var(--bloom-ink)] text-[#FAF4FA]"
                      : "text-[var(--bloom-ink-soft)] hover:bg-[var(--bloom-surface)] hover:text-[var(--bloom-ink)]"
                  }`}
                  style={{ borderRadius: 6 }}
                  data-testid={`grower-tab-${s.id}`}
                >
                  <span className="flex items-center gap-3">
                    <StatusIcon name={s.iconKey} size={20} fallback={<Icon size={16} strokeWidth={1.5} />} />
                    <span className="font-serif text-base">{s.label}</span>
                  </span>
                  {isActive && <ChevronRight size={14} />}
                </button>
              );
            })}
          </nav>
          <div className="p-4 border-t border-[var(--bloom-border)]">
            <button onClick={onLogout} className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-ink)]" data-testid="grower-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </aside>

        <main className="flex-1 min-w-0 px-6 md:px-12 py-10 pb-28 md:pb-10" data-testid="grower-main">
          {active === "dashboard" && <DashboardPanel dashboard={dashboard} setActive={setActive} />}
          {active === "profile" && <ProfilePanel dashboard={dashboard} />}
          {active === "settings" && <SettingsPanel dashboard={dashboard} navigate={navigate} />}
          {!["dashboard", "profile", "settings"].includes(active) && (
            <ComingSoonPanel sectionId={active} navigate={navigate} />
          )}
        </main>
      </div>
      <PublicFooter />
    </div>
  );
}

/* ---------- Panels ---------- */
function DashboardPanel({ dashboard, setActive }) {
  const app = dashboard.application;
  const m = dashboard.metrics;
  return (
    <div data-testid="grower-dashboard">
      <div className="mb-8">
        <div className="bloom-divider mb-4 text-left">✦ Grower HQ ✦</div>
        <h1 className="font-serif text-5xl md:text-6xl">Welcome back, <em>{app.farm_name}</em>.</h1>
        <p className="font-serif italic text-xl text-[var(--bloom-ink-soft)] mt-3">
          Your bloom command centre. More tools roll out as the season approaches.
        </p>
      </div>

      <div className="bloom-card p-6 md:p-8 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-5">
            <StatusIcon name="grower.active" size={88} fallback={<Sprout size={48} strokeWidth={1.2} className="text-[var(--bloom-brand)]" />} />
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-1">Active Grower</div>
              <div className="font-serif text-3xl">{app.farm_name}</div>
              <div className="font-serif italic text-[var(--bloom-ink-soft)] mt-1">
                {app.town_city}{app.region ? `, ${app.region}` : ""}
              </div>
              {app.activated_at && (
                <div className="font-serif italic text-sm text-[var(--bloom-ink-muted)] mt-2">
                  Activated {new Date(app.activated_at).toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" })}
                </div>
              )}
            </div>
          </div>
          <span className="px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase bg-[var(--bloom-ink)] text-[#FAF4FA]" style={{ borderRadius: 999 }}>
            Active
          </span>
        </div>
        {app.vision && (
          <p className="mt-5 pt-5 border-t border-[var(--bloom-border)] font-serif italic text-[var(--bloom-ink-soft)] leading-relaxed">
            "{app.vision}"
          </p>
        )}
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <KPI label="Subscribers matched" value={m.assigned_subscribers} sub={m.assigned_subscribers === 0 ? "Allocation opens before season" : "Active and paused"} />
        <KPI label="Upcoming deliveries" value={m.upcoming_deliveries} sub="None yet, season starts Jan 2027" />
        <KPI label="Current season" value={m.season_name} sub="Bloom Box Season" />
      </div>

      {/* Territory */}
      <TerritoryCard territories={dashboard.territories || []} region={app.region} />

      {/* Sticker grid */}
      <h2 className="font-serif text-2xl mb-4">Explore the portal</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {SECTIONS.filter((s) => s.id !== "dashboard").map((s) => {
          const Icon = s.icon;
          const isLive = s.live;
          return (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              className="bloom-card p-5 text-left transition hover:-translate-y-0.5 hover:shadow-lg relative"
              style={{ borderRadius: 8 }}
              data-testid={`grower-sticker-${s.id}`}
            >
              {!isLive && (
                <span className="absolute top-3 right-3 px-2 py-0.5 text-[8px] tracking-[0.2em] uppercase bg-[var(--bloom-surface)] text-[var(--bloom-ink-muted)]" style={{ borderRadius: 999 }}>
                  Soon
                </span>
              )}
              <StatusIcon
                name={s.iconKey}
                size={48}
                className="mb-3"
                fallback={<Icon size={28} strokeWidth={1.2} className="text-[var(--bloom-accent)] mb-3" />}
              />
              <div className="font-serif text-lg leading-tight">{s.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TerritoryCard({ territories, region }) {
  const hasTerritories = territories.length > 0;
  const STATUS_LABEL = { open: "Open", pending: "Coming soon", looking: "Looking" };
  return (
    <div className="bloom-card p-6 mb-8" data-testid="grower-map-card">
      <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-3">
        {hasTerritories ? `Your territor${territories.length > 1 ? "ies" : "y"}` : "Your region"}
      </div>
      {hasTerritories ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5" data-testid="grower-territory-cards">
            {territories.map((t) => (
              <div key={t.id} className="border border-[var(--bloom-border)] bg-white/50 p-4" data-testid={`grower-territory-card-${t.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-serif text-xl">{t.label}</div>
                  <span className={`px-2 py-0.5 text-[9px] tracking-[0.2em] uppercase ${t.status === "open" ? "bg-[var(--bloom-brand)] text-white" : t.status === "pending" ? "bg-[#C9A14A] text-white" : "bg-white border border-[var(--bloom-ink-soft)] text-[var(--bloom-ink-soft)]"}`} style={{ borderRadius: 999 }}>
                    {STATUS_LABEL[t.status] || t.status}
                  </span>
                </div>
                <div className="font-serif italic text-xs text-[var(--bloom-ink-muted)] mt-1">
                  {t.region} · {t.role === "primary" ? "Primary grower" : "Backup grower"}
                </div>
                <div className="flex gap-5 mt-3 text-sm">
                  <span><span className="font-serif text-lg">{t.real_waitlist}</span> <span className="text-xs text-[var(--bloom-ink-muted)]">on waitlist</span></span>
                  {t.capacity_subscribers > 0 && (
                    <span><span className="font-serif text-lg">{t.capacity_subscribers}</span> <span className="text-xs text-[var(--bloom-ink-muted)]">capacity</span></span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <NZTerritoryMap
            areas={territories}
            activeKey={`${territories[0].region}__${territories[0].city || ""}`}
            size="default"
            showLegend={true}
          />
        </>
      ) : (
        <>
          <p className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mb-4">
            You haven't been matched to a territory yet — Heritage HQ will assign you to an area on the map as the season approaches.
          </p>
          <NZTerritoryMap
            pins={region ? [{ region, count: 1 }] : []}
            activeRegion={region}
            size="default"
            showLegend={true}
          />
        </>
      )}
    </div>
  );
}

function ProfilePanel({ dashboard }) {
  const app = dashboard.application;
  return (
    <div data-testid="grower-profile-panel">
      <h1 className="font-serif text-4xl mb-2">Farm profile</h1>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">Your public-facing details on the Heritage Bloom Club platform.</p>
      <div className="bloom-card p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Farm name" value={app.farm_name} />
          <Field label="Town / city" value={app.town_city} />
          <Field label="Region" value={app.region} />
          <Field label="Application status" value={app.status} />
          <Field label="Activated" value={app.activated_at ? new Date(app.activated_at).toLocaleDateString("en-NZ") : "·"} />
          <Field label="Application ID" value={app.id?.slice(0, 8)} />
        </div>
        {app.vision && (
          <div className="mt-6 pt-6 border-t border-[var(--bloom-border)]">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bloom-ink-muted)] mb-2">Vision statement</div>
            <p className="font-serif italic text-[var(--bloom-ink-soft)] leading-relaxed">{app.vision}</p>
          </div>
        )}
        <div className="mt-6 pt-6 border-t border-[var(--bloom-border)] font-serif italic text-sm text-[var(--bloom-ink-muted)]">
          To update photos, vision, or details, please email the Heritage Bloom Club team. Edit in place coming in Phase D2.
        </div>
      </div>
    </div>
  );
}

function SettingsPanel({ dashboard, navigate }) {
  return (
    <div data-testid="grower-settings-panel">
      <h1 className="font-serif text-4xl mb-2">Settings</h1>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">Account preferences and access.</p>
      <div className="bloom-card p-6 md:p-8 mb-6">
        <h3 className="font-serif text-xl mb-3">Account access</h3>
        <p className="font-serif italic text-[var(--bloom-ink-soft)] text-sm mb-4">
          You signed up as a grower. To change your password, please contact the team.
        </p>
        <button onClick={() => navigate("/portal")} className="bloom-btn bloom-btn-outline">
          Back to applicant portal (Stage 2 prospectus)
        </button>
      </div>
    </div>
  );
}

function ComingSoonPanel({ sectionId, navigate }) {
  const section = SECTIONS.find((s) => s.id === sectionId);
  const Icon = section?.icon || Clock;
  return (
    <div data-testid={`grower-coming-soon-${sectionId}`}>
      <h1 className="font-serif text-4xl mb-2">{section?.label}</h1>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">
        Rolling out shortly with the season planning tools.
      </p>
      <div className="bloom-card p-12 text-center">
        <StatusIcon
          name={section?.iconKey}
          size={72}
          className="mx-auto mb-4"
          fallback={<Icon size={48} strokeWidth={1.2} className="mx-auto mb-4 text-[var(--bloom-accent)]" />}
        />
        <h3 className="font-serif text-2xl mb-2">Coming soon</h3>
        <p className="font-serif italic text-[var(--bloom-ink-soft)] max-w-md mx-auto">
          {COPY[sectionId] || "We're building this for you. Check back closer to season start."}
        </p>
      </div>
    </div>
  );
}

const COPY = {
  subscribers: "See every household matched to your farm: their preferences, delivery schedule, and gift notes. Activates once allocation runs in late 2026.",
  stem_forecast: "Tell us how many stems of each variety you'll have. Subscriber tiers are mapped against your forecast.",
  allocation: "View and confirm which subscribers you've been matched to, with bloom-by-bloom assignment details.",
  harvest_reports: "Track the season as it unfolds. Yields, varieties, week-by-week.",
  availability: "Mark holidays, harvest windows, frost events, and any breaks in supply.",
  guide: "Heritage Bloom Club's growers' playbook: standards, post-harvest care, packaging, delivery handover.",
  messages: "Direct line to your subscribers and the Heritage HQ team.",
  growth: "Track plant growth from corm to first bloom across your varieties.",
  delivery: "Your week-by-week delivery roster: dates, addresses, special notes.",
  earnings: "Per-delivery payouts, Stripe transfer history, and season totals.",
  network: "Meet the other Heritage Bloom Club growers. Share notes, swap stock, build community.",
};

/* ---------- Bits ---------- */
function KPI({ label, value, sub }) {
  return (
    <div className="bloom-card p-5">
      <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bloom-ink-muted)] mb-2">{label}</div>
      <div className="font-serif text-3xl">{value}</div>
      {sub && <div className="font-serif italic text-xs text-[var(--bloom-ink-muted)] mt-1">{sub}</div>}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bloom-ink-muted)] mb-1">{label}</div>
      <div className="font-serif text-base capitalize">{value || "·"}</div>
    </div>
  );
}
